import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  ComplaintConfirmation,
  ComplaintForm,
  ComplaintView,
  ComplaintTatListCell,
} from '../../features/complaints/components';
import { BarcodeScanner } from '../../features/devices/components';
import Pagination from '../../shared/components/common/Pagination';
import { DeviceIDBadge, ComplaintIDBadge, StatusBadge, SeverityBadge, CriticalityBadge } from '../../shared/components/common/Badges';
import { AvailabilityImpactBadge, AvailabilityImpactSignal } from '../../shared/components/common/AvailabilityImpactBadge';
import PersonBadge from '../../shared/components/common/PersonBadge';
import ExportButton from '../../shared/components/modals/ExportButton';
import ExportColumnConfigurationModal from '../../shared/components/modals/ExportColumnConfigurationModal';
import StatsCard from '../../shared/components/common/StatsCard';
import FilterHelpModal from '../../shared/components/modals/FilterHelpModal';
import MoreFiltersButton from '../../shared/components/common/MoreFiltersButton';
import {
  createInitialComplaintFilterState,
  createDefaultComplaintListFilterState,
  isComplaintsServerDateWindowActive,
  COMPLAINTS_DEFAULT_LOOKBACK_DAYS,
  hasActiveComplaintFilters,
} from '../../features/complaints/hooks';
import { formatAvailabilityImpactActiveFilterLabel, AVAILABILITY_IMPACT_FILTER_OPTIONS, deviceHasAvailabilityImpact } from '../../shared/constants/availabilityImpactFilter';
import { fetchFilteredComplaints, formatComplaintPaginationForApi } from '../../features/complaints/utils';
import {
  createComplaintExportColumnsState,
  getSelectedExportColumnHeaders,
} from '../../features/complaints/constants/complaintExportColumns';
import {
  PDF_EXPORT_MAX_COLUMNS,
  validatePdfExportColumnCount,
} from '../../constants/exportColumnPolicy';
import { exportFilteredComplaints } from '../../utils/exportUtils';
import { getDaysSinceNumeric } from '../../utils/dateUtils';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { getListPageSize, saveUiPreferencePatch, getComplaintsStatusFilter, getComplaintsSort } from '../../shared/utils/uiPreferences';
import {
  statusesFromComplaintsStatusFilter,
  sortingFromComplaintsSort,
} from '../../shared/utils/listDefaults';
import LocationFilterMultiSelect from '../../shared/components/common/LocationFilterMultiSelect';
import LocationDisabledBadge from '../../shared/components/common/LocationDisabledBadge';
import DisabledLocationAccessBlockedBanner from '../../shared/components/common/DisabledLocationAccessBlockedBanner';
import {
  findLocationById,
  getDisabledLocationsFromSelection,
  isLocationActive,
} from '../../shared/utils/locationDisplay';
import useBodyScrollLock from '../../shared/hooks/useBodyScrollLock';
import ModalCloseButton from '../../shared/components/modals/ModalCloseButton';
import ListSearchBar from '../../shared/components/common/ListSearchBar';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import './Complaints.css';

function AssignmentPersonChip({ children }) {
  return (
    <div className="person-section">
      <div className="person-chip-row">{children}</div>
    </div>
  );
}

/**
 * Complaints Component with Hybrid Filter Architecture
 *
 * Hybrid Filtering (Backend + Frontend):
 * - Tier 1 (Backend SQL): Status, Severity, Device IDs, Departments
 * - Tier 2 (Backend Calculations): Date-based filters
 * - Tier 3 (Backend Text Search): Complaint ID, title, details, device name, reporter
 * - Tier 4 (Frontend): Pagination, UI state
 *
 * Features:
 * - Server-side pagination with configurable per_page
 * - Advanced filters panel with 4-section architecture
 * - Active filters display with clear buttons
 * - Quick filter presets (coming soon)
 * - Export with column configuration
 * - Device scanning and complaint creation
 */
function Complaints({ token, initialComplaintId, onComplaintClose, initialStatusFilter, initialSeverityFilter, initialDaysOpenFrom, initialDaysOpenTo, initialDeviceFilter, initialSearchFilter, onViewFullDeviceDetails, onComplaintViewOpen, user }) {
  // ═══════════════════════════════════════════════════════════════════
  // SECTION A: CORE COMPLAINT DATA
  // ═══════════════════════════════════════════════════════════════════
  const [complaints, setComplaints] = useState([]);
  const [devices, setDevices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [complaintCategories, setComplaintCategories] = useState([]);
  const [complaintsSummary, setComplaintsSummary] = useState({
    open_count: 0,
    acknowledged_count: 0,
    in_progress_count: 0,
    escalated_to_vendor_count: 0,
    closed_count: 0,
    cancelled_count: 0
  });
  const [isRefreshingComplaints, setIsRefreshingComplaints] = useState(false);

  // ═══════════════════════════════════════════════════════════════════
  // SECTION B: FILTER STATE (4-Section Architecture)
  // ═══════════════════════════════════════════════════════════════════
  const [filters, setFilters] = useState(() => (
    createDefaultComplaintListFilterState(
      statusesFromComplaintsStatusFilter(getComplaintsStatusFilter())
    )
  ));
  const [activeFilterOrder, setActiveFilterOrder] = useState([]); // append-only ordering of activated filter keys

  const blockedDisabledLocations = useMemo(
    () => (user?.role === 'superadmin' ? getDisabledLocationsFromSelection(locations, filters.locations) : []),
    [user?.role, locations, filters.locations]
  );
  const isDisabledLocationFilterBlocked = blockedDisabledLocations.length > 0;

  // ═══════════════════════════════════════════════════════════════════
  // SECTION B2: PAGINATION STATE
  // ═══════════════════════════════════════════════════════════════════
  const getInitialPerPage = () => getListPageSize();

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: getInitialPerPage(),
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });

  // ═══════════════════════════════════════════════════════════════════
  // SECTION B3: SORTING STATE
  // ═══════════════════════════════════════════════════════════════════
  const [sorting, setSorting] = useState(() => sortingFromComplaintsSort(getComplaintsSort()));

  // ═══════════════════════════════════════════════════════════════════
  // SECTION C: UI STATE
  // ═══════════════════════════════════════════════════════════════════
  const [loading, setLoading] = useState(true);
  const [showNewComplaintForm, setShowNewComplaintForm] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [complaintConfirmation, setComplaintConfirmation] = useState(null);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [deviceSearchTerm, setDeviceSearchTerm] = useState('');
  const [viewingComplaintFullPage, setViewingComplaintFullPage] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerKey, setScannerKey] = useState(0);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showExportColumnsConfig, setShowExportColumnsConfig] = useState(false);
  const [expandedFilterSections, setExpandedFilterSections] = useState({
    statusSeverity: true,
    location: false,
    deviceDepartment: true,
    dates: true,
    search: false
  });
  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState(createInitialComplaintFilterState());
  const [showFilterHelp, setShowFilterHelp] = useState(false);

  // Track whether "All" statuses were explicitly selected from navigation (empty filter)
  // This helps distinguish between "showing all statuses" vs "no filters applied"
  const [isShowingAllStatusesFromNavigation, setIsShowingAllStatusesFromNavigation] = useState(false);

  const [exportColumns, setExportColumns] = useState(() => createComplaintExportColumnsState('excel'));
  const [isExportingComplaints, setIsExportingComplaints] = useState(false);

  // Lock background scroll while the create-complaint device selector is open
  useBodyScrollLock(showDeviceSelector);

  const exportMenuRef = useRef(null);
  const isSyncingPropsRef = useRef(false);  // Track if we're currently syncing props to avoid double-fetch

  // ═══════════════════════════════════════════════════════════════════
  // SECTION D: ROLE-BASED ACCESS
  // ═══════════════════════════════════════════════════════════════════
  const isCustodian = user && user.role === 'custodian';
  const isCoordinator = user && user.role === 'coordinator';
  const isTechnician = user && user.role === 'technician';
  const isManager = user && user.role === 'manager';
  const isAdmin = user && user.role === 'admin';
  const isCentralHelpDesk = user && user.role === 'central_help_desk';

  const { canPerformFeature } = useRoleAccess(user);

  const canAddComplaint = isCustodian || isTechnician || isManager || isCentralHelpDesk || isCoordinator || isAdmin;
  const canScanDevice = isCustodian || isTechnician || isManager || isCentralHelpDesk || isCoordinator || isAdmin;
  const canManageComplaints = canAddComplaint || canScanDevice;
  const canSeeFilters = true;
  // Align with backend export_complaints whitelist (includes superadmin; excludes technician)
  const canExport = canPerformFeature('export_complaints');

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 0: Sync props to state AND fetch immediately
  // Prevents double-fetch by combining state sync with fetch
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (token && user) {
      // Mark that we're syncing props (so EFFECT 1 doesn't also fetch)
      isSyncingPropsRef.current = true;

      // Check if props are providing actual filter values
      const hasStatusFilterFromProps = initialStatusFilter !== null && initialStatusFilter !== undefined;
      const hasSeverityFilterFromProps = initialSeverityFilter !== null && initialSeverityFilter !== undefined;
      const hasDeviceFilterFromProps = initialDeviceFilter !== null && initialDeviceFilter !== undefined && initialDeviceFilter.length > 0;
      const hasSearchFilterFromProps = initialSearchFilter !== null && initialSearchFilter !== undefined && String(initialSearchFilter).trim() !== '';

      // Convert props to arrays if they have values; otherwise use open-tickets default preference
      const propStatusFilter = hasStatusFilterFromProps
        ? (Array.isArray(initialStatusFilter) ? initialStatusFilter : [initialStatusFilter])
        : statusesFromComplaintsStatusFilter(getComplaintsStatusFilter());

      const propSeverityFilter = hasSeverityFilterFromProps
        ? (Array.isArray(initialSeverityFilter) ? initialSeverityFilter : [initialSeverityFilter])
        : [];

      const propDeviceFilter = hasDeviceFilterFromProps
        ? (Array.isArray(initialDeviceFilter) ? initialDeviceFilter : [initialDeviceFilter])
        : [];

      const propSearchFilter = hasSearchFilterFromProps ? String(initialSearchFilter).trim() : '';

      const propShowAllFlag = hasStatusFilterFromProps
        ? propStatusFilter.length === 0
        : propStatusFilter.length === 0;

      // Default status chips: open tickets (incl. acknowledged). Date window is server-side.
      const filtersWithProps = {
        ...createDefaultComplaintListFilterState(propStatusFilter),
        severities: propSeverityFilter,
        deviceIds: propDeviceFilter,
        search: propSearchFilter,
        daysOpenFrom: initialDaysOpenFrom || '',
        daysOpenTo: initialDaysOpenTo || '',
      };

      // Sync to state
      setFilters(filtersWithProps);
      setIsShowingAllStatusesFromNavigation(propShowAllFlag);

      // Fetch immediately with the prop values (don't wait for state update)
      const currentPerPage = pagination.per_page || 10;
      fetchComplaints(filtersWithProps, { page: 1, per_page: currentPerPage });
      fetchDepartments();

      // Mark sync complete so EFFECT 1 can run on next change
      setTimeout(() => { isSyncingPropsRef.current = false; }, 0);
    }
  }, [token, user, initialStatusFilter, initialSeverityFilter, initialDaysOpenFrom, initialDaysOpenTo, initialDeviceFilter, initialSearchFilter]);

  const isFilterKeyActive = (filtersObj, key) => {
    switch (key) {
      case 'statuses':
        return (filtersObj.statuses || []).length > 0;
      case 'severities':
        return (filtersObj.severities || []).length > 0;
      case 'impactsAvailability':
        return !!(filtersObj.impactsAvailability && filtersObj.impactsAvailability !== '');
      case 'filedByMe':
        return Boolean(filtersObj.filedByMe);
      case 'locations':
        return (filtersObj.locations || []).length > 0;
      case 'deviceIds':
        return (filtersObj.deviceIds || []).length > 0;
      case 'departments':
        return (filtersObj.departments || []).length > 0;
      case 'categoryIds':
        return (filtersObj.categoryIds || []).length > 0;
      case 'createdDateRange':
        return !!(filtersObj.createdDateFrom || filtersObj.createdDateTo);
      case 'resolutionDateRange':
        return !!(filtersObj.resolutionDateFrom || filtersObj.resolutionDateTo);
      case 'daysOpenRange':
        return !!(filtersObj.daysOpenFrom || filtersObj.daysOpenTo);
      default:
        return false;
    }
  };

  // Maintain append-only order of filters as the user activates them
  useEffect(() => {
    const allKeys = [
      'statuses',
      'severities',
      'impactsAvailability',
      'filedByMe',
      'locations',
      'deviceIds',
      'departments',
      'categoryIds',
      'createdDateRange',
      'resolutionDateRange',
      'daysOpenRange'
    ];

    setActiveFilterOrder(prevOrder => {
      let nextOrder = prevOrder.filter(key => allKeys.includes(key));
      nextOrder = nextOrder.filter(key => isFilterKeyActive(filters, key));

      for (const key of allKeys) {
        if (isFilterKeyActive(filters, key) && !nextOrder.includes(key)) {
          nextOrder = [...nextOrder, key];
        }
      }

      return nextOrder;
    });
  }, [filters]);

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 1: Fetch complaints when filters or sorting changes from user interaction
  // This handles quick filters, advanced filters, search, and sorting - works for all users
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Skip if we just synced props (EFFECT 0 already fetched)
    if (isSyncingPropsRef.current) {
      return;
    }

    // Fetch on filter or sort changes caused by user interaction - works for all users
    if (token && user) {
      const currentPerPage = pagination.per_page || 10;
      fetchComplaints(filters, { page: 1, per_page: currentPerPage });
      fetchDepartments();
    }
  }, [token, user, filters, sorting.sort_by, sorting.sort_direction]);

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 2: Fetch complaints when pagination changes (same filters)
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Skip if we just synced props (EFFECT 0 already fetched)
    // This prevents double-fetch during initial mount from dashboard navigation
    if (isSyncingPropsRef.current) {
      return;
    }

    // Fetch when pagination changes (page or per_page) - works for all users
    if (token && user) {
      // First param: filtersOverride (null = use current filters)
      // Second param: paginationOverride (pass new pagination)
      fetchComplaints(null, pagination);
    }
  }, [pagination.page, pagination.per_page, user]);

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 3: Fetch devices on mount - works for all users
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (token && user) {
      fetchDevices();
    }
  }, [token, user]);

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 4: Handle initial complaint view (from App.js)
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (initialComplaintId) {
      setViewingComplaintFullPage(initialComplaintId);
    }
  }, [initialComplaintId]);

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 2.5: Close export menu when clicking outside
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 2.6: Manage body overflow when advanced filter panel opens/closes
  // Prevents background page from scrolling when filter drawer is open
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (showAdvancedPanel) {
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      // Restore background scrolling
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showAdvancedPanel]);

  // ═══════════════════════════════════════════════════════════════════
  // DATA FETCHING FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Fetch filtered and paginated complaints from backend
   * @param {Object} filtersOverride - Optional filters to use instead of state
   * @param {Object} paginationOverride - Optional pagination params
   */
  const fetchComplaints = async (filtersOverride = null, paginationOverride = null) => {
    try {
      setIsRefreshingComplaints(true);

      const filtersToUse = filtersOverride || filters;

      if (user?.role === 'superadmin' && getDisabledLocationsFromSelection(locations, filtersToUse.locations).length > 0) {
        setComplaints([]);
        setComplaintsSummary({
          open_count: 0,
          acknowledged_count: 0,
          in_progress_count: 0,
          closed_count: 0,
          cancelled_count: 0,
        });
        setPagination((prev) => ({
          ...prev,
          total_count: 0,
          total_pages: 0,
          has_next: false,
          has_previous: false,
        }));
        setLoading(false);
        return;
      }

      const paginationParams = paginationOverride || pagination;
      const formattedPagination = formatComplaintPaginationForApi(paginationParams.page, paginationParams.per_page);

      // Build sorting parameter (only if sort_by is set)
      const sortingParam = sorting.sort_by ? {
        sort_by: sorting.sort_by,
        sort_direction: sorting.sort_direction
      } : null;

      // Call backend filter endpoint with sorting
      const result = await fetchFilteredComplaints(filtersToUse, formattedPagination, token, sortingParam);

      // Update complaints list
      setComplaints(result.complaints || []);

      // Update summary data (stats for entire filtered set)
      if (result.summary) {
        setComplaintsSummary(result.summary);
      }

      // Update pagination metadata
      setPagination({
        page: result.pagination.page,
        per_page: result.pagination.per_page,
        total_count: result.pagination.total_count,
        total_pages: result.pagination.total_pages,
        has_next: result.pagination.has_next,
        has_previous: result.pagination.has_previous
      });

      setLoading(false);
    } catch (error) {
      console.error('[Complaints.js] Error fetching complaints:', error);

      if (error.code === 'UNAUTHORIZED') {
        toast.error('Session expired. Please login again.');
      } else if (error.code === 'FORBIDDEN') {
        toast.error('You do not have permission to view complaints.');
      } else if (error.code === 'NETWORK_ERROR') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(error.message || 'Failed to fetch complaints');
      }

      setComplaints([]);
      setPagination(prev => ({ ...prev, total_count: 0, total_pages: 0 }));
      setLoading(false);
    } finally {
      setIsRefreshingComplaints(false);
    }
  };

  /**
   * Fetch devices for device selector
   */
  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.DEVICES.LIST}?per_page=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDevices(response.data.devices || []);
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  /**
   * Fetch departments for filters
   */
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.DEPARTMENTS.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  /**
   * Fetch locations for filters (superadmin only)
   */
  const fetchLocationsData = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOCATIONS.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    }
  };

  /**
   * Fetch complaint categories for the category filter (active catalog).
   * Available to all roles with complaints module access.
   */
  const fetchComplaintCategories = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.COMPLAINT_CATEGORIES.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setComplaintCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setComplaintCategories([]);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 3.5: Fetch locations (superadmin) + complaint categories (all roles)
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!token) return;
    if (user?.role === 'superadmin') {
      fetchLocationsData();
    }
    fetchComplaintCategories();
  }, [token, user?.role]);

  // ═══════════════════════════════════════════════════════════════════
  // UI EVENT HANDLERS
  // ═══════════════════════════════════════════════════════════════════

  const handleViewComplaint = (complaintId) => {
    setViewingComplaintFullPage(complaintId);
    if (onComplaintViewOpen) {
      onComplaintViewOpen(complaintId);
    }
  };

  const handleAddComplaint = () => {
    setDeviceSearchTerm('');
    setShowDeviceSelector(true);
  };

  const handleDeviceSelect = (device) => {
    if (device.device_status === 'in_transfer') {
      toast.info('Complaints cannot be created while this device is in a transfer');
      return;
    }
    setSelectedDevice(device);
    setShowDeviceSelector(false);
    setShowNewComplaintForm(true);
    setDeviceSearchTerm('');
  };

  const handleCloseDeviceSelector = () => {
    setShowDeviceSelector(false);
    setDeviceSearchTerm('');
  };

  const handleCloseComplaintForm = () => {
    setShowNewComplaintForm(false);
    setSelectedDevice(null);
  };

  const handleComplaintSuccess = (confirmationPayload) => {
    if (confirmationPayload) {
      setComplaintConfirmation(confirmationPayload);
    }
    fetchComplaints();
  };

  const handleScanDevice = async (scannedCode) => {
    setShowScanner(false);

    try {
      let deviceIdentifier = null;

      // Check if scanned code is a full URL (for QR codes with base64 tokens)
      if (scannedCode.includes('/device-scan')) {
        try {
          const url = new URL(scannedCode);
          // Extract token from URL path - it's the last segment after /device-scan
          // Format: https://yourdomain.com/device-scan?token={token}
          let tokenParam = url.searchParams.get('token');

          if (tokenParam) {
            // CRITICAL FIX: Backend handles base64 decoding, not frontend
            // Frontend should call the QR endpoint which decodes the token server-side
            // This prevents cryptographic manipulation and ensures proper decoding
            try {
              // SECURITY FIX: /api/devices/qr/{token} endpoint does NOT require authentication
              // because it validates organization/device ownership via base64-encoded token only
              // Token format: base64(org_id|device_identifier)
              const qrDeviceResponse = await axios.get(
                API_ENDPOINTS.DEVICES.QR_DEVICE(tokenParam)
                // No auth header needed - token provides all necessary context and validation
              );

              // Extract device identifier from the decoded response
              if (qrDeviceResponse.data && qrDeviceResponse.data.device_identifier) {
                deviceIdentifier = qrDeviceResponse.data.device_identifier;
              } else {
                console.error('Invalid response from QR device endpoint:', qrDeviceResponse.data);
                toast.error('Failed to parse the scanned QR code. Please verify the code is not damaged or expired.');
                return;
              }
            } catch (decodeError) {
              console.error('Error decoding QR token via backend:', decodeError);

              // Check if it's an authentication or network error
              if (decodeError.response?.status === 401 || decodeError.response?.status === 403) {
                toast.error('You do not have permission to access this QR code.');
              } else {
                toast.error(decodeError.response?.data?.message || 'Failed to parse the scanned QR code. Please verify the code is not damaged or expired.');
              }
              return;
            }
          }
        } catch (urlError) {
          console.error('Error parsing scanned URL:', urlError);
          toast.error('Failed to parse the scanned QR code. Please verify the code is not damaged or expired.');
          return;
        }
      }

      if (deviceIdentifier) {
        const encodedCode = encodeURIComponent(deviceIdentifier);
        const response = await axios.get(`${API_ENDPOINTS.DEVICES.LIST}/lookup/id/${encodedCode}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        let device = response.data;

        // Enrich device data by merging with full device details from the already-fetched devices list
        // This ensures all fields (name, department, department_icon, etc.) are populated
        if (devices && devices.length > 0) {
          const fullDeviceData = devices.find(d =>
            d.id === device.id ||
            d.device_identifier === device.device_identifier ||
            d.serial_number === device.serial_number
          );

          if (fullDeviceData) {
            // Merge API response with full device data, API response takes precedence
            device = {
              ...fullDeviceData,
              ...device
            };
          }
        }

        if (device.device_status === 'in_transfer') {
          toast.info('Complaints cannot be created while this device is in a transfer');
          return;
        }

        setSelectedDevice(device);
        setShowNewComplaintForm(true);
      } else {
        toast.error("The QR code is invalid. Please check the code and try again!");
      }
    } catch (error) {
      console.error('Error looking up device:', error);
      if (error.response?.status === 404) {
        const errorMsg = error.response?.data?.error || `No device found with code: ${scannedCode}`;
        if (errorMsg.includes('not accessible in your department')) {
          toast.error('Device not found in your department');
        } else {
          toast.error(errorMsg);
        }
      } else {
        toast.error('Failed to lookup device');
      }
    }
  };

  const handleOpenScanner = () => {
    setScannerKey(prev => prev + 1);
    setShowScanner(true);
  };

  /**
   * Handle sort column change from table headers
   * Updates sorting state which triggers EFFECT 1 to fetch with new sort
   */
  const handleSortChange = (column) => {
    setSorting(prev => {
      // If same column clicked, toggle direction; otherwise set new column with asc
      if (prev.sort_by === column) {
        return {
          ...prev,
          sort_direction: prev.sort_direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        return {
          sort_by: column,
          sort_direction: 'asc'
        };
      }
    });
  };

  /**
   * Get sort indicator for column header
   * Uses current sorting state to determine active sort column and direction
   */
  const getSortIndicator = (column) => {
    if (!sorting || sorting.sort_by !== column) return '';
    return sorting.sort_direction === 'asc' ? ' ▲' : ' ▼';
  };

  // ═══════════════════════════════════════════════════════════════════
  // QUICK FILTER PRESET FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Helper functions for checking individual preset conditions
   * These allow each quick filter button to independently check if its
   * condition is met, regardless of how filters were set
   */
  const isOpenActive = () => {
    // Don't highlight individual status if all statuses are selected in clean state
    if (isAllStatusesSelectedClean()) return false;
    return filters.statuses?.includes('open');
  };

  const isAcknowledgedActive = () => {
    if (isAllStatusesSelectedClean()) return false;
    return filters.statuses?.includes('acknowledged');
  };

  const isInProgressActive = () => {
    // Don't highlight individual status if all statuses are selected in clean state
    if (isAllStatusesSelectedClean()) return false;
    return filters.statuses?.includes('in_progress');
  };

  const isEscalatedToVendorActive = () => {
    if (isAllStatusesSelectedClean()) return false;
    return filters.statuses?.includes('escalated_to_vendor');
  };

  const isImpactsAvailabilityActive = () => filters.impactsAvailability === 'yes';

  const isFiledByMeActive = () => Boolean(filters.filedByMe);

  const isCriticalActive = () => {
    // Critical is a severity filter, show when applied regardless of status state
    return filters.severities?.includes('critical');
  };

  const isRecentlyResolved48hActive = () => {
    // Check if filtering for closed status with last 48 hours date range
    if (!filters.statuses?.includes('closed')) return false;
    const fortyEightHoursAgo = new Date();
    fortyEightHoursAgo.setDate(fortyEightHoursAgo.getDate() - 2);
    const expectedFrom = fortyEightHoursAgo.toISOString().split('T')[0];
    return filters.resolutionDateFrom === expectedFrom;
  };

  const isRecentlyResolved7daysActive = () => {
    // Check if filtering for closed status with last 7 days date range
    if (!filters.statuses?.includes('closed')) return false;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const expectedFrom = sevenDaysAgo.toISOString().split('T')[0];
    return filters.resolutionDateFrom === expectedFrom;
  };

  const isRecentlyResolved30daysActive = () => {
    // Check if filtering for closed status with last 30 days date range
    if (!filters.statuses?.includes('closed')) return false;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const expectedFrom = thirtyDaysAgo.toISOString().split('T')[0];
    return filters.resolutionDateFrom === expectedFrom;
  };

  /**
   * Check if no filters are applied (showing all complaints)
   * This is true when:
   * 1. Statuses array is empty (meaning "show all statuses")
   * 2. No severity, department, device, or date filters are applied
   * (Free-text search does not count — clear via search box only)
   */
  const isAllStatusesSelectedClean = () => {
    const isDefaultState =
      (filters.statuses?.length === 0 || !filters.statuses) &&
      (filters.severities?.length === 0 || !filters.severities) &&
      (filters.deviceIds?.length === 0 || !filters.deviceIds) &&
      (filters.departments?.length === 0 || !filters.departments) &&
      !filters.impactsAvailability &&
      !filters.filedByMe &&
      !filters.createdDateFrom &&
      !filters.createdDateTo &&
      !filters.resolutionDateFrom &&
      !filters.resolutionDateTo &&
      !filters.daysOpenFrom &&
      !filters.daysOpenTo;

    return isDefaultState;
  };

  const COMPLAINT_QUICK_STATUSES = ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor', 'closed'];
  const hasAdditionalFilters = Boolean(
    (filters.locations || []).length
    || (filters.deviceIds || []).length
    || (filters.departments || []).length
    || (filters.categoryIds || []).length
    || filters.createdDateFrom
    || filters.createdDateTo
    || filters.daysOpenFrom
    || filters.daysOpenTo
    || (filters.severities || []).some((severity) => severity !== 'critical')
    || (filters.statuses || []).some((status) => !COMPLAINT_QUICK_STATUSES.includes(status))
    || (filters.impactsAvailability && filters.impactsAvailability !== 'yes')
    || (
      (filters.resolutionDateFrom || filters.resolutionDateTo)
      && !isRecentlyResolved48hActive()
      && !isRecentlyResolved7daysActive()
      && !isRecentlyResolved30daysActive()
    )
  );

  /**
   * Apply quick filter preset with simple toggle behavior
   *
   * STATUS FILTER LOGIC (Simplified):
   * - Empty statuses array = show all statuses (default state)
   * - Status filters: simple toggle - click to add/remove from array
   * - Severity filters: toggle on/off
   * - Compound preset (High Priority) replaces all filters
   * - Recently Resolved replaces all filters
   * - All clears all filters
   */
  const applyPreset = (presetName) => {
    switch (presetName) {
      case 'all':
        // Clear status/other chips; server default date window still applies
        setFilters(createInitialComplaintFilterState());
        // Reset the navigation flag since user explicitly clicked "All" button
        setIsShowingAllStatusesFromNavigation(false);
        break;
      case 'open':
        // Simple toggle: add or remove 'open' from statuses
        setFilters(prev => {
          const newStatuses = prev.statuses.includes('open')
            ? prev.statuses.filter(s => s !== 'open')
            : [...prev.statuses, 'open'];
          return { ...prev, statuses: newStatuses };
        });
        break;
      case 'acknowledged':
        setFilters(prev => {
          const newStatuses = prev.statuses.includes('acknowledged')
            ? prev.statuses.filter(s => s !== 'acknowledged')
            : [...prev.statuses, 'acknowledged'];
          return { ...prev, statuses: newStatuses };
        });
        break;
      case 'in-progress':
        // Simple toggle: add or remove 'in_progress' from statuses
        setFilters(prev => {
          const newStatuses = prev.statuses.includes('in_progress')
            ? prev.statuses.filter(s => s !== 'in_progress')
            : [...prev.statuses, 'in_progress'];
          return { ...prev, statuses: newStatuses };
        });
        break;
      case 'escalated-to-vendor':
        setFilters(prev => {
          const newStatuses = prev.statuses.includes('escalated_to_vendor')
            ? prev.statuses.filter(s => s !== 'escalated_to_vendor')
            : [...prev.statuses, 'escalated_to_vendor'];
          return { ...prev, statuses: newStatuses };
        });
        break;
      case 'impacts-availability':
        setFilters(prev => ({
          ...prev,
          impactsAvailability: prev.impactsAvailability === 'yes' ? '' : 'yes',
        }));
        break;
      case 'filed-by-me':
        setFilters(prev => ({
          ...prev,
          filedByMe: !prev.filedByMe,
        }));
        break;
      case 'critical':
        // Toggle 'critical' severity - add or remove it from existing severities
        setFilters(prev => {
          const newSeverities = prev.severities.includes('critical')
            ? prev.severities.filter(s => s !== 'critical')
            : [...prev.severities, 'critical'];
          return { ...prev, severities: newSeverities };
        });
        break;
      case 'high-priority':
        // Compound preset: replaces all filters with open/in-progress + critical
        // If already active, toggle it off
        if (
          filters.statuses.includes('open') &&
          filters.statuses.includes('in_progress') &&
          filters.statuses.includes('escalated_to_vendor') &&
          filters.severities.includes('critical') &&
          filters.statuses.length === 3 &&
          filters.severities.length === 1
        ) {
          setFilters(createInitialComplaintFilterState());
        } else {
          setFilters(prev => ({
            ...prev,
            statuses: ['open', 'in_progress', 'escalated_to_vendor'],
            severities: ['critical']
          }));
        }
        break;
      case 'recently-resolved-48h':
        // Show closed complaints from the last 48 hours
        // If already active, toggle it off
        if (isRecentlyResolved48hActive()) {
          setFilters(createInitialComplaintFilterState());
        } else {
          const fortyEightHoursAgo = new Date();
          fortyEightHoursAgo.setDate(fortyEightHoursAgo.getDate() - 2);
          const today = new Date();
          setFilters(prev => ({
            ...prev,
            statuses: ['closed'],
            resolutionDateFrom: fortyEightHoursAgo.toISOString().split('T')[0],
            resolutionDateTo: today.toISOString().split('T')[0]
          }));
        }
        break;
      case 'recently-resolved-7days':
        // Show closed complaints from the last 7 days
        // If already active, toggle it off
        if (isRecentlyResolved7daysActive()) {
          setFilters(createInitialComplaintFilterState());
        } else {
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          const today = new Date();
          setFilters(prev => ({
            ...prev,
            statuses: ['closed'],
            resolutionDateFrom: sevenDaysAgo.toISOString().split('T')[0],
            resolutionDateTo: today.toISOString().split('T')[0]
          }));
        }
        break;
      case 'recently-resolved-30days':
        // Show closed complaints from the last 30 days
        // If already active, toggle it off
        if (isRecentlyResolved30daysActive()) {
          setFilters(createInitialComplaintFilterState());
        } else {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          const today = new Date();
          setFilters(prev => ({
            ...prev,
            statuses: ['closed'],
            resolutionDateFrom: thirtyDaysAgo.toISOString().split('T')[0],
            resolutionDateTo: today.toISOString().split('T')[0]
          }));
        }
        break;
      default:
        break;
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // FILTER HELPERS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Helper to render filter section header with tooltip
   * Shows "ⓘ" icon that explains AND/OR logic
   */
  const FilterSectionHeader = ({ icon, title, tooltip }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-start' }}>
      <span>{icon}</span>
      <span>{title}</span>
      {tooltip && (
        <span
          className="filter-section-tooltip"
          title={tooltip}
          style={{
            cursor: 'help',
            opacity: 0.6,
            fontSize: '0.85rem',
            fontWeight: 'bold'
          }}
        >
          ⓘ
        </span>
      )}
    </div>
  );

  const hasActiveFilters = () => hasActiveComplaintFilters(filters);

  const filteredDevices = devices.filter(device => {
    const searchLower = deviceSearchTerm.toLowerCase();
    return (
      device.serial_number.toLowerCase().includes(searchLower) ||
      device.name.toLowerCase().includes(searchLower) ||
      device.physical_location.toLowerCase().includes(searchLower) ||
      (device.department && device.department.toLowerCase().includes(searchLower))
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };


  const handleExportExcel = async () => {
    if (!token) return;
    const selected = getSelectedExportColumnHeaders(exportColumns);
    if (selected.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    setIsExportingComplaints(true);
    try {
      const { exportedRowCount } = await exportFilteredComplaints(filters, sorting, 'excel', token, selected);
      const count = exportedRowCount ?? pagination.total_count;
      toast.success(
        count > 0
          ? `Exported ${count} complaint${count !== 1 ? 's' : ''} to Excel`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error(error.message || 'Failed to export Excel');
    } finally {
      setIsExportingComplaints(false);
    }
  };

  const handleExportPDF = async () => {
    if (!token) return;
    const selected = getSelectedExportColumnHeaders(exportColumns);
    if (selected.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    const pdfCheck = validatePdfExportColumnCount(selected.length);
    if (!pdfCheck.ok) {
      toast.error(pdfCheck.message);
      return;
    }
    setIsExportingComplaints(true);
    try {
      const { exportedRowCount } = await exportFilteredComplaints(filters, sorting, 'pdf', token, selected);
      const count = exportedRowCount ?? pagination.total_count;
      toast.success(
        count > 0
          ? `Exported ${count} complaint${count !== 1 ? 's' : ''} to PDF`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(error.message || 'Failed to export PDF');
    } finally {
      setIsExportingComplaints(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════

  if (loading) {
    return <div className="loading">Loading complaints...</div>;
  }

  // Show full-page complaint view if one is selected
  if (viewingComplaintFullPage) {
    return (
      <ComplaintView
        complaintId={viewingComplaintFullPage}
        token={token}
        onClose={() => {
          if (user && user.role === 'technician' && onComplaintClose) {
            onComplaintClose();
          } else {
            setViewingComplaintFullPage(null);
          }
        }}
        onUpdate={fetchComplaints}
        onViewFullDeviceDetails={onViewFullDeviceDetails}
        isFullPage={true}
        user={user}
      />
    );
  }

  return (
    <div className="complaints-container">
      <div className="complaints-description-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1>⚠️ Device Breakdown Complaints</h1>
          <button onClick={() => fetchComplaints()} className="btn-refresh" title="Refresh complaints" style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}>
            🔄
          </button>
        </div>
        <p>
          {isTechnician
            ? 'Your assigned work queue by default. Filed by me shows tickets you reported.'
            : 'Report, track, and manage device breakdowns and complaints. Monitor complaint status, assign technicians, and document resolution for all reported issues.'}
        </p>
      </div>

      {isComplaintsServerDateWindowActive(filters) && (
        <div className="list-default-window-note" role="note">
          <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
          <span>
            Showing open tickets (any age) and closed/cancelled from the{' '}
            <strong>last {COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days</strong>.
            Use Advanced Filters → Created date to include older closed/cancelled records.
          </span>
        </div>
      )}

      <div className="complaints-header">
        <div className="header-left">
          <div className="stats-summary">
            <StatsCard
              icon="📋"
              number={pagination.total_count}
              label={(() => {
                if (!isTechnician) {
                  return `Total${hasActiveFilters() ? ' (Filtered)' : ''}`;
                }
                const scopeLabel = isFiledByMeActive() ? 'Filed by you' : 'Work queue';
                const hasExtraFilters = hasActiveComplaintFilters({ ...filters, filedByMe: false });
                return `${scopeLabel}${hasExtraFilters ? ' (Filtered)' : ''}`;
              })()}
              className="total"
            />
            <StatsCard
              icon="🔴"
              number={complaintsSummary.open_count}
              label="Open"
              className="open"
            />
            <StatsCard
              icon="🟡"
              number={complaintsSummary.acknowledged_count}
              label="Ack"
              className="acknowledged"
            />
            <StatsCard
              icon="🔵"
              number={complaintsSummary.in_progress_count}
              label="In Progress"
              className="progress"
            />
            <StatsCard
              icon="🚩"
              number={complaintsSummary.escalated_to_vendor_count ?? 0}
              label="Escalated"
              className="escalated"
            />
            <StatsCard
              icon="✅"
              number={complaintsSummary.closed_count}
              label="Closed"
              className="closed"
            />
            <StatsCard
              icon="✗"
              number={complaintsSummary.cancelled_count}
              label="Cancelled"
              className="cancelled"
            />
          </div>
        </div>
        <div className="header-right">
          <div className="action-palette">
            {canScanDevice && (
              <button className="action-btn action-secondary" onClick={handleOpenScanner}>
                <span className="action-icon">📷</span>
                <span className="action-text">Scan Device</span>
              </button>
            )}
            {canAddComplaint && (
              <button className="action-btn action-primary" onClick={handleAddComplaint}>
                <span className="action-icon">➕</span>
                <span className="action-text">Add Complaint</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="search-section">
        <div className="search-box-wrapper">
          <span className="search-icon-lens">🔍</span>
          <input
            type="text"
            placeholder={filters.deviceIds && filters.deviceIds.length > 0 ? "Device ID filter applied. Type to search..." : "Search complaints by complaint ID, device ID, device name, title, or details..."}
            value={filters.deviceIds && filters.deviceIds.length > 0 ? filters.deviceIds.join(', ') : filters.search}
            onChange={(e) => {
              // When user types, clear device filter and update search
              if (filters.deviceIds && filters.deviceIds.length > 0) {
                setFilters({ ...filters, search: e.target.value, deviceIds: [] });
              } else {
                setFilters({ ...filters, search: e.target.value });
              }
            }}
            className="search-input-clean"
          />
          {(filters.search || (filters.deviceIds && filters.deviceIds.length > 0)) && (
            <button
              className="clear-search-btn-inline"
              onClick={() => setFilters({ ...filters, search: '', deviceIds: [] })}
              title="Clear search and device filter"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* QUICK FILTERS (PRESETS) */}
      <div className="quick-filters">
        <div className="quick-filters-title">
          <svg className="filters-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Quick Filters
        </div>
        <div className="preset-buttons">
          <button
            className={`preset-btn ${isAllStatusesSelectedClean() ? 'active' : ''}`}
            onClick={() => applyPreset('all')}
            title={
              isTechnician
                ? 'Show all of your assigned complaints (default date window still applies)'
                : 'Show all statuses (default date window still applies)'
            }
          >
            All
          </button>
          <button
            className={`preset-btn ${isOpenActive() ? 'active' : ''}`}
            onClick={() => applyPreset('open')}
            title="Show only open complaints"
          >
            Open
          </button>
          <button
            className={`preset-btn ${isAcknowledgedActive() ? 'active' : ''}`}
            onClick={() => applyPreset('acknowledged')}
            title="Show only acknowledged complaints"
          >
            Acknowledged
          </button>
          <button
            className={`preset-btn ${isInProgressActive() ? 'active' : ''}`}
            onClick={() => applyPreset('in-progress')}
            title="Show only in-progress complaints"
          >
            In Progress
          </button>
          <button
            className={`preset-btn ${isEscalatedToVendorActive() ? 'active' : ''}`}
            onClick={() => applyPreset('escalated-to-vendor')}
            title="Show complaints escalated to vendor"
          >
            Escalated to Vendor
          </button>
          <button
            className={`preset-btn ${isImpactsAvailabilityActive() ? 'active' : ''}`}
            onClick={() => applyPreset('impacts-availability')}
            title="Show complaints impacting clinical availability — matches purple square on Status"
          >
            <span className="availability-impact-signal availability-impact-signal--yes" aria-hidden="true" />
            Availability Impact
          </button>
          <button
            className={`preset-btn ${isCriticalActive() ? 'active' : ''}`}
            onClick={() => applyPreset('critical')}
            title="Show only critical severity complaints"
          >
            Critical
          </button>
          <button
            className={`preset-btn ${isFiledByMeActive() ? 'active' : ''}`}
            onClick={() => applyPreset('filed-by-me')}
            title={
              isTechnician
                ? 'Show tickets you reported (may overlap with your work queue, or include ones assigned to someone else)'
                : 'Show complaints you reported'
            }
          >
            Filed by me
          </button>
          <button
            className={`preset-btn ${isRecentlyResolved48hActive() ? 'active' : ''}`}
            onClick={() => applyPreset('recently-resolved-48h')}
            title="Show complaints resolved in the last 48 hours"
          >
            Resolved (Last 48h)
          </button>
          <button
            className={`preset-btn ${isRecentlyResolved7daysActive() ? 'active' : ''}`}
            onClick={() => applyPreset('recently-resolved-7days')}
            title="Show complaints resolved in the last 7 days"
          >
            Resolved (Last 7d)
          </button>
          <button
            className={`preset-btn ${isRecentlyResolved30daysActive() ? 'active' : ''}`}
            onClick={() => applyPreset('recently-resolved-30days')}
            title="Show complaints resolved in the last 30 days"
          >
            Resolved (Last 30d)
          </button>
          <MoreFiltersButton
            isOpen={showAdvancedPanel}
            hasAdditional={hasAdditionalFilters}
            onClick={() => {
              if (!showAdvancedPanel) {
                setDrawerFilters({ ...filters });
              }
              setShowAdvancedPanel(!showAdvancedPanel);
            }}
            onHelpClick={() => setShowFilterHelp(true)}
          />
        </div>
      </div>

      {/* ACTIVE FILTERS DISPLAY - Only shown when filters are active */}
      {canSeeFilters && hasActiveFilters() && (
        <div className="active-filters">
          <div className="active-filters-header">
            <span className="active-filters-label">Filters Active:</span>
            <button
              className="clear-all-btn"
              onClick={() => {
                // Preserve free-text search — clear only structured filters
                // Clear chips; server default date window still applies
                setFilters({
                  ...createInitialComplaintFilterState(),
                  search: filters.search,
                });
                setActiveFilterOrder([]);
              }}
            >
              Clear All
            </button>
          </div>

          <div className="active-filters-pills">
            {(() => {
              const activeFilterPills = [];

              const pushWithAnd = (node) => {
                if (activeFilterPills.length > 0) {
                  activeFilterPills.push(
                    <span key={`and-${activeFilterPills.length}`} className="filter-and-connector"> AND </span>
                  );
                }
                activeFilterPills.push(node);
              };

              for (const key of activeFilterOrder) {
                if (key === 'statuses' && filters.statuses.length > 0) {
                  pushWithAnd(
                    <div key="status-pill" className="filter-pill">
                      Status: {filters.statuses.join(', ')}
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({ ...filters, statuses: [] })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }

                if (key === 'severities' && filters.severities.length > 0) {
                  pushWithAnd(
                    <div key="severity-pill" className="filter-pill">
                      Severity: {filters.severities.join(', ')}
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({ ...filters, severities: [] })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }

                if (key === 'impactsAvailability' && filters.impactsAvailability) {
                  pushWithAnd(
                    <div key="availability-pill" className="filter-pill">
                      {formatAvailabilityImpactActiveFilterLabel(filters.impactsAvailability)}
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({ ...filters, impactsAvailability: '' })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }

                if (key === 'filedByMe' && filters.filedByMe) {
                  pushWithAnd(
                    <div key="filed-by-me-pill" className="filter-pill">
                      Filed by me
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({ ...filters, filedByMe: false })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }

                if (key === 'locations' && filters.locations && filters.locations.length > 0) {
                  pushWithAnd(
                    <div key="loc-pill" className="filter-pill">
                      <span className="filter-pill-location-list">
                        Location:{' '}
                        {filters.locations.map((locId, index) => {
                          const location = findLocationById(locations, locId);
                          return (
                            <span key={locId} className="filter-pill-location-item">
                              {index > 0 ? ', ' : ''}
                              {location ? location.name : locId}
                              {location && !isLocationActive(location) && <LocationDisabledBadge />}
                            </span>
                          );
                        })}
                      </span>
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({ ...filters, locations: [] })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }

                if (key === 'deviceIds' && filters.deviceIds && filters.deviceIds.length > 0) {
                  pushWithAnd(
                    <div key="device-pill" className="filter-pill">
                      Device: {filters.deviceIds.join(', ')}
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({ ...filters, deviceIds: [] })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }

                if (key === 'departments' && filters.departments.length > 0) {
                  pushWithAnd(
                    <div key="dept-pill" className="filter-pill">
                      Department: {filters.departments.join(', ')}
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({ ...filters, departments: [] })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }

                if (key === 'categoryIds' && filters.categoryIds && filters.categoryIds.length > 0) {
                  pushWithAnd(
                    <div key="category-pill" className="filter-pill">
                      Category: {filters.categoryIds.map((catId) => {
                        const cat = complaintCategories.find((c) => String(c.id) === String(catId));
                        return cat ? cat.label : catId;
                      }).join(', ')}
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({ ...filters, categoryIds: [] })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }

                if (key === 'createdDateRange' && (filters.createdDateFrom || filters.createdDateTo)) {
                  pushWithAnd(
                    <div key="created-pill" className="filter-pill">
                      Created: {filters.createdDateFrom || '…'} to {filters.createdDateTo || '…'}
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({
                          ...filters,
                          createdDateFrom: '',
                          createdDateTo: '',
                        })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }

                if (key === 'resolutionDateRange' && (filters.resolutionDateFrom || filters.resolutionDateTo)) {
                  pushWithAnd(
                    <div key="resolved-pill" className="filter-pill">
                      Resolved: {filters.resolutionDateFrom} to {filters.resolutionDateTo}
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({ ...filters, resolutionDateFrom: '', resolutionDateTo: '' })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }

                if (key === 'daysOpenRange' && (filters.daysOpenFrom || filters.daysOpenTo)) {
                  pushWithAnd(
                    <div key="days-pill" className="filter-pill">
                      Days Open: {filters.daysOpenFrom} to {filters.daysOpenTo}
                      <span
                        className="filter-pill-remove"
                        onClick={() => setFilters({ ...filters, daysOpenFrom: '', daysOpenTo: '' })}
                      >
                        ✕
                      </span>
                    </div>
                  );
                }
              }

              return activeFilterPills;
            })()}
          </div>
        </div>
      )}


      {/* REACTDOM PORTAL - Advanced Filters Modal */}
      {showAdvancedPanel && ReactDOM.createPortal(
        <div className="sidebar-drawer-overlay" onClick={() => setShowAdvancedPanel(false)}>
          <div className="sidebar-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-title">
              <span>🔧 Advanced Filters</span>
              <button
                className="drawer-close-btn"
                onClick={() => setShowAdvancedPanel(false)}
                title="Close filters"
              >
                ✕
              </button>
            </div>

            <div className="drawer-filter-content">
            {/* SECTION 1: LOCATION (SUPERADMIN ONLY) - FIRST FILTER */}
            {user?.role === 'superadmin' && (
              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedFilterSections(prev => ({ ...prev, location: !prev.location }))}
                >
                  <span className={`group-arrow ${expandedFilterSections.location ? 'open' : ''}`}>▼</span>
                  <FilterSectionHeader
                    icon="📍"
                    title="Location"
                    tooltip="Select multiple locations. Complaints from ANY selected location are shown (OR logic)"
                  />
                </div>
                {expandedFilterSections.location && (
                  <div className="filter-group-content">
                    <div className="filter-item-wrapper">
                      <LocationFilterMultiSelect
                        locations={locations}
                        value={drawerFilters.locations || []}
                        onChange={(selected) =>
                          setDrawerFilters({
                            ...drawerFilters,
                            locations: selected,
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SECTION 2: STATUS & SEVERITY */}
            <div className="filter-group">
              <div
                className="filter-group-title"
                onClick={() => setExpandedFilterSections(prev => ({ ...prev, statusSeverity: !prev.statusSeverity }))}
              >
                <span className={`group-arrow ${expandedFilterSections.statusSeverity ? 'open' : ''}`}>▼</span>
                📊 Status & Severity
              </div>
              {expandedFilterSections.statusSeverity && (
                <div className="filter-group-content">
                  <div className="filter-item-wrapper">
                    <div className="filter-label-text">Status</div>
                    <select
                      className="filter-select"
                      multiple
                      value={drawerFilters.statuses || []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setDrawerFilters({ ...drawerFilters, statuses: selected });
                      }}
                      style={{ minHeight: '120px' }}
                    >
                      <option value="open">Open</option>
                      <option value="acknowledged">Acknowledged</option>
                      <option value="in_progress">In Progress</option>
                      <option value="escalated_to_vendor">Escalated to Vendor</option>
                      <option value="closed">Closed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div className="filter-item-wrapper">
                    <div className="filter-label-text">Availability Impact</div>
                    <select
                      className="filter-select"
                      value={drawerFilters.impactsAvailability || ''}
                      onChange={(e) => {
                        setDrawerFilters({ ...drawerFilters, impactsAvailability: e.target.value });
                      }}
                    >
                      {AVAILABILITY_IMPACT_FILTER_OPTIONS.map((option) => (
                        <option key={option.value || 'all'} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-item-wrapper">
                    <div className="filter-label-text">Severity</div>
                    <select
                      className="filter-select"
                      multiple
                      value={drawerFilters.severities || []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setDrawerFilters({ ...drawerFilters, severities: selected });
                      }}
                      style={{ minHeight: '100px' }}
                    >
                      <option value="critical">Critical</option>
                      <option value="major">Major</option>
                      <option value="minor">Minor</option>
                    </select>
                  </div>

                  {/* Category filter - only rendered when categories are available (currently superadmin-only) */}
                  {complaintCategories.length > 0 && (
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Category</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.categoryIds || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({ ...drawerFilters, categoryIds: selected });
                        }}
                        style={{ minHeight: '100px' }}
                      >
                        {complaintCategories.map((category) => (
                          <option key={category.id} value={category.id}>{category.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* SECTION 3: DEPARTMENT */}
            <div className="filter-group">
              <div
                className="filter-group-title"
                onClick={() => setExpandedFilterSections(prev => ({ ...prev, deviceDepartment: !prev.deviceDepartment }))}
              >
                <span className={`group-arrow ${expandedFilterSections.deviceDepartment ? 'open' : ''}`}>▼</span>
                🏢 Department
              </div>
              {expandedFilterSections.deviceDepartment && (
                <div className="filter-group-content">
                  <div className="filter-item-wrapper">
                    <div className="filter-label-text">Departments</div>
                    <select
                      className="filter-select"
                      multiple
                      value={drawerFilters.departments || []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setDrawerFilters({ ...drawerFilters, departments: selected });
                      }}
                      style={{ minHeight: '100px' }}
                      disabled={user && ['coordinator', 'custodian', 'technician'].includes(user.role) && user.departments && user.departments.length === 1}
                      title={user && ['coordinator', 'custodian', 'technician'].includes(user.role) && user.departments && user.departments.length === 1 ? 'Only your assigned department is available' : ''}
                    >
                      {/* For department-scoped users (coordinator, custodian, technician): ONLY show their assigned departments */}
                      {/* Managers are location-scoped, so they see all departments like admin/central_help_desk */}
                      {user && ['coordinator', 'custodian', 'technician'].includes(user.role) ? (
                        user.departments && user.departments.length > 0 ? (
                          user.departments.map((dept) => (
                            <option key={dept.id} value={dept.name}>
                              {dept.icon} {dept.name}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>No departments assigned</option>
                        )
                      ) : (
                        /* For admin/manager/superadmin/central_help_desk: show all departments from API fetch */
                        departments.map(dept => (
                          <option key={dept.id} value={dept.name}>{dept.icon} {dept.name}</option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 4: DATES */}
            <div className="filter-group">
              <div
                className="filter-group-title"
                onClick={() => setExpandedFilterSections(prev => ({ ...prev, dates: !prev.dates }))}
              >
                <span className={`group-arrow ${expandedFilterSections.dates ? 'open' : ''}`}>▼</span>
                📅 Date Ranges
              </div>
              {expandedFilterSections.dates && (
                <div className="filter-group-content">
                  <div className="filter-item-wrapper">
                    <div className="filter-label-text">Reported Date Range</div>
                    <div className="filter-range">
                      <input
                        type="date"
                        placeholder="From"
                        max={new Date().toISOString().split('T')[0]}
                        value={drawerFilters.createdDateFrom || ''}
                        onChange={(e) => {
                          if (drawerFilters.createdDateTo && e.target.value > drawerFilters.createdDateTo) {
                            toast.error('From date cannot be greater than To date');
                          } else {
                            setDrawerFilters({ ...drawerFilters, createdDateFrom: e.target.value });
                          }
                        }}
                      />
                      <span className="filter-range-separator">-</span>
                      <input
                        type="date"
                        placeholder="To"
                        max={new Date().toISOString().split('T')[0]}
                        value={drawerFilters.createdDateTo || ''}
                        onChange={(e) => {
                          if (drawerFilters.createdDateFrom && e.target.value < drawerFilters.createdDateFrom) {
                            toast.error('To date must be greater than or equal to From date');
                          } else {
                            setDrawerFilters({ ...drawerFilters, createdDateTo: e.target.value });
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="filter-item-wrapper">
                    <div className="filter-label-text">Resolution Date Range</div>
                    <div className="filter-range">
                      <input
                        type="date"
                        placeholder="From"
                        max={new Date().toISOString().split('T')[0]}
                        value={drawerFilters.resolutionDateFrom || ''}
                        onChange={(e) => {
                          if (drawerFilters.resolutionDateTo && e.target.value > drawerFilters.resolutionDateTo) {
                            toast.error('From date cannot be greater than To date');
                          } else {
                            setDrawerFilters({ ...drawerFilters, resolutionDateFrom: e.target.value });
                          }
                        }}
                      />
                      <span className="filter-range-separator">-</span>
                      <input
                        type="date"
                        placeholder="To"
                        max={new Date().toISOString().split('T')[0]}
                        value={drawerFilters.resolutionDateTo || ''}
                        onChange={(e) => {
                          if (drawerFilters.resolutionDateFrom && e.target.value < drawerFilters.resolutionDateFrom) {
                            toast.error('To date must be greater than or equal to From date');
                          } else {
                            setDrawerFilters({ ...drawerFilters, resolutionDateTo: e.target.value });
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="filter-item-wrapper full-width">
                    <div className="filter-label-text">Days Open</div>
                    <div className="filter-range">
                      <input
                        type="number"
                        placeholder="Min days"
                        value={drawerFilters.daysOpenFrom || ''}
                        onChange={(e) => {
                          const minValue = e.target.value ? Number(e.target.value) : '';
                          setDrawerFilters({ ...drawerFilters, daysOpenFrom: minValue });
                        }}
                      />
                      <span className="filter-range-separator">-</span>
                      <input
                        type="number"
                        placeholder="Max days"
                        value={drawerFilters.daysOpenTo || ''}
                        onChange={(e) => {
                          const maxValue = e.target.value ? Number(e.target.value) : '';
                          setDrawerFilters({ ...drawerFilters, daysOpenTo: maxValue });
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="drawer-buttons">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowAdvancedPanel(false);
                }}
                title="Close filters without applying changes"
              >
                Close
              </button>
              <button
                className="reset-btn"
                onClick={() => {
                  setDrawerFilters(createInitialComplaintFilterState());
                }}
              >
                Reset
              </button>
              <button
                className="apply-btn"
                onClick={() => {
                  // Validate Days Open filter
                  if (drawerFilters.daysOpenFrom !== '' && drawerFilters.daysOpenTo !== '') {
                    const minDays = Number(drawerFilters.daysOpenFrom);
                    const maxDays = Number(drawerFilters.daysOpenTo);
                    if (minDays > maxDays) {
                      toast.error('Minimum days cannot be greater than Maximum days');
                      return;
                    }
                  }

                  setFilters({ ...drawerFilters });
                  setShowAdvancedPanel(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isDisabledLocationFilterBlocked && (
        <DisabledLocationAccessBlockedBanner disabledLocations={blockedDisabledLocations} />
      )}

      {complaints.length === 0 ? (
        <div className="empty-state">
          <p>
            {isDisabledLocationFilterBlocked
              ? 'Operational data is unavailable for the selected disabled location.'
              : 'No complaints found matching the selected filters.'}
          </p>
        </div>
      ) : (
        <>
          <div className="complaints-results-info">
            Showing {complaints.length} of {pagination.total_count} complaints
            {canExport && pagination.total_count > 0 && (
              <span> — Export includes all {pagination.total_count} filtered complaints</span>
            )}
          </div>

          {canExport && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              paddingBottom: '1rem',
              marginBottom: '1rem',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <ExportButton
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                title="Export filtered complaints"
              />
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.25rem',
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: '1'
                }}
                onClick={() => setShowExportColumnsConfig(true)}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                }}
                title="Configure export columns"
              >
                ⚙️
              </button>
            </div>
          )}

          <div className="complaints-table-container" style={{ position: 'relative' }}>
            {isRefreshingComplaints && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '4px'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid var(--dp-accent-primary)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <div style={{ color: 'var(--dp-accent-primary)', fontWeight: '600', fontSize: '0.95rem' }}>
                    Refreshing complaints...
                  </div>
                </div>
              </div>
            )}
            <table className="complaints-table">
              <thead>
                <tr>
                  <th
                    className="complaint-status-col"
                    onClick={() => handleSortChange('status')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by workflow status"
                  >
                    <div className="complaint-status-col-header">
                      <span className="complaint-status-col-title">
                        Status{getSortIndicator('status')}
                      </span>
                      <div
                        className="complaint-status-col-legend"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span
                          className="complaint-status-col-legend-item"
                          title="Purple square = clinical availability impacted"
                        >
                          <span className="availability-impact-signal availability-impact-signal--yes" aria-hidden="true" />
                          Impacted
                        </span>
                        <span
                          className="complaint-status-col-legend-item"
                          title="Amber square = impact not confirmed yet (before work starts)"
                        >
                          <span className="availability-impact-signal availability-impact-signal--pending" aria-hidden="true" />
                          Pending
                        </span>
                      </div>
                    </div>
                  </th>
                  <th
                    className="center"
                    onClick={() => handleSortChange('severity')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by severity"
                  >
                    Severity{getSortIndicator('severity')}
                  </th>
                  <th
                    onClick={() => handleSortChange('category_label')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by category"
                  >
                    Category{getSortIndicator('category_label')}
                  </th>
                  <th
                    onClick={() => handleSortChange('title')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by complaint title"
                  >
                    Complaint{getSortIndicator('title')}
                  </th>
                  <th
                    onClick={() => handleSortChange('device_name')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by device name"
                  >
                    Device{getSortIndicator('device_name')}
                  </th>
                  <th
                    onClick={() => handleSortChange('department')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by department"
                  >
                    Department{getSortIndicator('department')}
                  </th>
                  <th
                    className="complaint-dates-col"
                    onClick={() => handleSortChange('updated_at')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Updated date (Reported shown below) — click to sort"
                  >
                    Updated{getSortIndicator('updated_at')}
                  </th>
                  <th className="complaint-tat-col">
                    <div className="complaint-tat-col-header">
                      <span className="complaint-tat-col-title">TAT</span>
                      <span className="complaint-tat-col-help">Elapsed time</span>
                      <span className="complaint-tat-col-legend" aria-label="SLA legend">
                        <span className="complaint-tat-col-legend-item">
                          <span className="tat-sla-mark tat-sla-mark--met" aria-hidden="true">✓</span>
                          Within SLA
                        </span>
                        <span className="complaint-tat-col-legend-item">
                          <span className="tat-sla-mark tat-sla-mark--missed" aria-hidden="true">⚠</span>
                          Out of SLA
                        </span>
                      </span>
                    </div>
                  </th>
                  <th className="complaint-assignment-col">
                    <div className="complaint-assignment-col-header">
                      <span className="complaint-assignment-col-title">Assignment</span>
                      <span className="complaint-assignment-col-legend" aria-label="Assignment legend">
                        <span className="complaint-assignment-col-legend-item">
                          <span
                            className="assignment-legend-swatch assignment-legend-swatch--reporter"
                            aria-hidden="true"
                          />
                          Reporter
                        </span>
                        <span className="complaint-assignment-col-legend-item">
                          <span
                            className="assignment-legend-swatch assignment-legend-swatch--assignee"
                            aria-hidden="true"
                          />
                          Assignee
                        </span>
                      </span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {complaints.map((complaint) => {
                  return (
                    <tr
                      key={complaint.id}
                      className={`severity-${complaint.severity || 'minor'}`}
                    >
                      <td className="complaint-status-col">
                        <div className="complaint-status-cell">
                          <StatusBadge status={complaint.status || 'unknown'} size="small" />
                          <AvailabilityImpactSignal complaint={complaint} />
                        </div>
                      </td>
                      <td className="center">
                        <SeverityBadge severity={complaint.severity} size="small" />
                      </td>
                      <td>
                        <span title={complaint.category_label || complaint.category_code || undefined}>
                          {complaint.category_label || '—'}
                        </span>
                      </td>
                      <td className="complaint-stacked-cell">
                        <div className="stacked-content">
                          {complaint.location_name && (
                            <span
                              className="device-location"
                              style={{ fontWeight: '500', fontSize: '0.8rem', color: '#6c757d', marginTop: '4px' }}
                              title={`Location: ${complaint.location_name}`}
                            >
                              📍 {complaint.location_name}
                            </span>
                          )}
                          <span className="complaint-table-title">
                            {complaint.title}
                          </span>
                          <ComplaintIDBadge
                            complaintId={complaint.complaint_identifier}
                            clickable={true}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewComplaint(complaint.id);
                            }}
                            size="small"
                            title="Click to view complaint details"
                          />
                          {(complaint.document_count || 0) > 0 && (
                            <span
                              className="complaint-attachment-indicator"
                              title={`${complaint.document_count} attachment${complaint.document_count !== 1 ? 's' : ''}`}
                              aria-label={`${complaint.document_count} attachment${complaint.document_count !== 1 ? 's' : ''}`}
                            >
                              📎
                              <span className="complaint-attachment-count">
                                {complaint.document_count}
                              </span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="device-stacked-cell">
                        <div className="stacked-content">
                          <span className="device-name">{complaint.device_name}</span>
                          <DeviceIDBadge
                            deviceId={complaint.device_identifier}
                            clickable={complaint.device_navigable !== false}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (complaint.device_navigable === false) return;
                              onViewFullDeviceDetails && onViewFullDeviceDetails({ id: complaint.device_id, serial_number: complaint.serial_number });
                            }}
                            size="small"
                            data-device-badge
                            title={
                              complaint.device_navigable === false && complaint.device_current_location_name
                                ? `Device transferred to ${complaint.device_current_location_name}`
                                : undefined
                            }
                          />
                          {complaint.device_navigable === false && complaint.device_current_location_name && (
                            <span className="device-transferred-notice" role="status">
                              Transferred to {complaint.device_current_location_name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="department-cell">
                          <span className="department-icon-small" aria-hidden="true">
                            {complaint.department_icon || '🏢'}
                          </span>
                          <span className="department-text">{complaint.department || 'N/A'}</span>
                        </span>
                      </td>
                      <td
                        className="complaint-dates-col"
                        title={[
                          complaint.updated_at
                            ? `Updated: ${new Date(complaint.updated_at).toLocaleString()}`
                            : null,
                          complaint.created_at
                            ? `Reported: ${new Date(complaint.created_at).toLocaleString()}`
                            : null,
                        ].filter(Boolean).join(' | ')}
                      >
                        <div className="complaint-dates-cell">
                          <div className="complaint-date-primary">
                            {formatDate(complaint.updated_at || complaint.created_at)}
                          </div>
                          {complaint.created_at && (
                            <div className="complaint-date-secondary">
                              <span>Reported {formatDate(complaint.created_at)}</span>
                              {(() => {
                                const daysSince = getDaysSinceNumeric(complaint.created_at);
                                const displayText = daysSince === 0 ? 'Today' : `${daysSince}d ago`;
                                let ageClass = 'reported-age';
                                if (daysSince > 2) ageClass += ' reported-age--stale';
                                else if (daysSince > 1) ageClass += ' reported-age--aging';

                                return (
                                  <span className={ageClass} title={`${displayText} since reported`}>
                                    {displayText}
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="complaint-tat-col">
                        <ComplaintTatListCell complaint={complaint} />
                      </td>
                      <td className="reported-assigned-cell">
                        <div className="reported-assigned-container">
                          <AssignmentPersonChip>
                            <PersonBadge
                              name={complaint.reporter_full_name || complaint.reporter_employee_id || 'N/A'}
                              icon="👤"
                              badgeType="default"
                              isAssigned={true}
                              triggerMode="iconClick"
                            />
                          </AssignmentPersonChip>
                          {complaint.assigned_employee_name ? (
                            <AssignmentPersonChip>
                              <PersonBadge
                                name={complaint.assigned_employee_name}
                                icon="⚙️"
                                badgeType="assigned"
                                isAssigned={true}
                                triggerMode="iconClick"
                              />
                            </AssignmentPersonChip>
                          ) : (
                            <AssignmentPersonChip>
                              <PersonBadge
                                name="Unassigned"
                                icon="⊘"
                                badgeType="unassigned"
                                isAssigned={false}
                              />
                            </AssignmentPersonChip>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

          {pagination.total_count > 0 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.total_pages}
              onPageChange={(newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
              perPage={pagination.per_page}
              onPerPageChange={(newPerPage) => {
                saveUiPreferencePatch({ list_page_size: newPerPage });
                setPagination(prev => ({ ...prev, page: 1, per_page: newPerPage }));
                // FIXED: Pass pagination as second parameter, not first
                // Keep current filters and use new pagination
                fetchComplaints(null, { page: 1, per_page: newPerPage });
              }}
              totalItems={pagination.total_count}
              itemLabel="complaints"
            />
          )}
          </div>
        </>
      )}

      {showDeviceSelector && ReactDOM.createPortal(
        <div className="modal-overlay">
          <div className="device-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="device-selector-modal-header">
              <h3>
                <span className="device-selector-modal-title-icon" aria-hidden="true">🖥️</span>
                Select Device
              </h3>
              <ModalCloseButton onClick={handleCloseDeviceSelector} />
            </div>
            <div className="device-selector-content">
              <div className="selector-header-row">
                <p className="selector-subtitle">Choose the device to report a complaint for:</p>
                {deviceSearchTerm && (
                  <span className="device-count-badge">
                    {filteredDevices.length} device{filteredDevices.length !== 1 ? 's' : ''} found
                  </span>
                )}
              </div>

              <ListSearchBar
                value={deviceSearchTerm}
                onChange={setDeviceSearchTerm}
                onClear={() => setDeviceSearchTerm('')}
                placeholder="Search by serial number, device name, location, or department..."
                autoFocus
              />

              <div className="device-list-selector">
                {filteredDevices.length > 0 ? (
                  filteredDevices.map((device) => {
                    const isInTransfer = device.device_status === 'in_transfer';
                    const isLocationTransfer = Boolean(device.transferring_to_location_name);
                    const isDepartmentTransfer = Boolean(device.transferring_to_department_name);
                    const transferTitle = isLocationTransfer
                      ? 'Complaints cannot be created during a location transfer'
                      : isDepartmentTransfer
                        ? 'Complaints cannot be created during a department transfer'
                        : 'Complaints cannot be created while this device is in a transfer';
                    return (
                    <div
                      key={device.id}
                      className={`device-selector-item${isInTransfer ? ' device-selector-item--in-transfer' : ''}`}
                      onClick={() => handleDeviceSelect(device)}
                      aria-disabled={isInTransfer}
                      title={isInTransfer ? transferTitle : undefined}
                    >
                      <div className="device-selector-info">
                        <span className="device-selector-name">{device.name}</span>
                        <div className="device-selector-codes">
                          <DeviceIDBadge deviceId={device.device_identifier || 'N/A'} size="small" />
                          {device.criticality && (
                            <CriticalityBadge criticality={device.criticality} size="small" />
                          )}
                          {deviceHasAvailabilityImpact(device) && (
                            <AvailabilityImpactBadge deviceHasImpact size="small" />
                          )}
                        </div>
                        {isInTransfer && (
                          <span className="device-in-transfer-notice" role="status">
                            {device.transferring_to_location_name
                              ? `🚚 Transferring to ${device.transferring_to_location_name}`
                              : device.transferring_to_department_name
                                ? `🔁 Transferring to ${device.transferring_to_department_name}`
                                : 'In transfer'}
                          </span>
                        )}
                      </div>
                      <div className="device-selector-meta">
                        {device.department && (
                          <>
                            <div>
                              <span className="department-icon">{device.department_icon || '🏥'}</span>
                              <span className="device-selector-department">
                                <span className="department-name">{device.department}</span>
                              </span>
                            </div>
                          </>
                        )}
                        <span className="device-selector-location">{device.physical_location}</span>
                      </div>
                    </div>
                    );
                  })
                ) : (
                  <div className="no-devices-found">
                    <p>No devices found matching "{deviceSearchTerm}"</p>
                    <button className="action-btn-sm action-secondary" onClick={() => setDeviceSearchTerm('')}>
                      Clear Search
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showNewComplaintForm && selectedDevice && (
        <ComplaintForm
          device={selectedDevice}
          token={token}
          onClose={handleCloseComplaintForm}
          onSuccess={handleComplaintSuccess}
          user={user}
        />
      )}

      {complaintConfirmation && (
        <ComplaintConfirmation
          confirmation={complaintConfirmation}
          user={user}
          onClose={() => setComplaintConfirmation(null)}
          onViewComplaint={(id) => {
            setComplaintConfirmation(null);
            handleViewComplaint(id);
          }}
        />
      )}

      {showScanner && (
        <BarcodeScanner
          key={scannerKey}
          onScan={handleScanDevice}
          onClose={() => setShowScanner(false)}
          title="Scan Device"
          subtitle="Align the QR code within the frame to look up a device."
        />
      )}

      <ExportColumnConfigurationModal
        isOpen={showExportColumnsConfig}
        onClose={() => setShowExportColumnsConfig(false)}
        title="Configure Export Columns"
        description="A minimal operational set is pre-selected. Add or remove columns as needed. Excel supports any number of columns; PDF is limited for readable landscape layout."
        columns={exportColumns}
        onColumnsChange={setExportColumns}
        pdfMaxColumns={PDF_EXPORT_MAX_COLUMNS}
      />

      {/* Filter Help Modal */}
      <FilterHelpModal
        isOpen={showFilterHelp}
        onClose={() => setShowFilterHelp(false)}
        filterType="complaints"
      />
    </div>
  );
}

export default Complaints;
